// Document signing domain types — signable-PDF templates and contact-targeted signature requests.
// This module MUST stay self-contained (no imports) so it compiles cleanly under both the app's
// `bundler` resolution and functions' `nodenext` resolution. See /shared/tsconfig.json.
//
// Runtime literals (feed names, size limits) are mirrored in functions/src/documents/constants.ts —
// functions cannot import runtime values from this declaration-only package, only types.

export type SignableFieldType = 'signature' | 'initials' | 'date';

/** One placed box. Coordinates are fractions of the page CropBox, origin TOP-LEFT. */
export interface SignableField {
  id: string; // uuid v4, client-generated
  pageIndex: number; // 0-based
  type: SignableFieldType;
  x: number;
  y: number; // top-left corner, 0..1
  w: number;
  h: number; // size, 0..1
  label?: string; // optional caption shown in the box, e.g. "Talent initials"
}

export interface SignableTemplatePage {
  index: number; // 0-based
  widthPt: number; // CropBox width in PDF points
  heightPt: number; // CropBox height in PDF points
  originXPt: number; // CropBox lower-left x (0 when CropBox == MediaBox at origin)
  originYPt: number; // CropBox lower-left y
  pngStoragePath: string; // signableTemplates/{templateId}/pages/page-{index+1}.png
  pngDownloadUrl: string; // durable download-token URL (saveFileAndGetUrl)
  pngWidth: number; // rendered pixel size (for sanity checks / aspect)
  pngHeight: number;
}

/** `deleting` is set transactionally by `deleteSignableTemplate` so no new request can snapshot the template mid-delete. */
export type SignableTemplateStatus = 'processing' | 'ready' | 'error' | 'deleting';

/** Firestore: signableTemplates/{templateId} */
export interface SignableTemplate {
  id: string;
  name: string;
  description?: string;
  status: SignableTemplateStatus;
  errorMessage?: string | null; // set when status === 'error'
  pdfStoragePath: string; // signableTemplates/{templateId}/source.pdf
  pdfDownloadUrl: string; // client getDownloadURL at upload time
  pdfFileName: string; // original file name
  pageCount: number; // 0 until processed
  pages: SignableTemplatePage[]; // [] until processed
  fields: SignableField[]; // [] until staff place boxes
  created: Date | null;
  modified: Date | null;
  createdByUid: string;
  createdByName: string;
}

export type DocumentRequestStatus = 'draft' | 'sent' | 'viewed' | 'signed' | 'revoked';

export interface DocumentRequestSignature {
  storagePath: string; // documentRequests/{requestId}/attempts/{attemptId}/signature.png | initials.png
  downloadUrl: string;
  width: number; // PNG pixel dims (from IHDR) — for display only
  height: number;
}

/** Firestore: documentRequests/{requestId} */
export interface DocumentRequest {
  id: string;
  // --- template snapshot (frozen at creation; see D6) ---
  templateId: string;
  documentName: string;
  templatePdfStoragePath: string;
  templatePdfDownloadUrl: string;
  pages: SignableTemplatePage[];
  fields: SignableField[];
  // --- target ---
  contactId: string;
  contactName: string; // "First Last" at creation
  contactEmail: string; // address actually used for the send (editable in dialog)
  audience: 'client' | 'talent'; // drives link host (D12)
  // --- lifecycle ---
  status: DocumentRequestStatus;
  signingToken: string; // 64-hex, plaintext, unique (looked up with ==)
  signingTokenCreatedAt: Date | null;
  sentAt: Date | null; // first delivery (email or link copy)
  sentByName: string | null;
  sentByContactId: string | null; // requester — gets the in-app ping on sign (D13)
  sentVia: 'email' | 'link' | null;
  lastSentAt: Date | null; // last email send (resend)
  linkCopiedAt: Date | null;
  linkCopiedByName: string | null;
  viewedAt: Date | null;
  signedAt: Date | null;
  signedByName: string | null;
  signature: DocumentRequestSignature | null;
  initials: DocumentRequestSignature | null;
  signedStoragePath: string | null; // documentRequests/{requestId}/attempts/{attemptId}/signed.pdf
  signedDownloadUrl: string | null;
  /** Claimed transactionally by `signDocumentByToken` before any artifact is written; the final
   *  commit is rejected if another attempt (or a revoke) has superseded it. */
  signingAttemptId: string | null;
  signingAttemptStartedAt: Date | null;
  ipAddress: string | null;
  userAgent: string | null;
  revokedAt: Date | null;
  revokedByName: string | null;
  created: Date | null;
  modified: Date | null;
}

// --- constants (also inlined in functions/src/documents/constants.ts) ---
export const SIGNABLE_TEMPLATE_MAX_PAGES = 30;
export const SIGNABLE_TEMPLATE_MAX_BYTES = 20 * 1024 * 1024;
export const DOCUMENT_REQUEST_EMAIL_TEMPLATE = 'Document Signature Request';
export const DOCUMENT_VIEWED_FEED = 'Contact Viewed Document';
export const DOCUMENT_SIGNED_FEED = 'Contact Signed Document';

/** Default box sizes as fraction of page WIDTH; height derives from aspect (D19). */
export const FIELD_DEFAULTS: Record<SignableFieldType, { w: number; aspect: number | null; h?: number }> = {
  signature: { w: 0.28, aspect: 3 }, // h = w * (widthPt/heightPt) / 3
  initials: { w: 0.08, aspect: 1 }, // h = w * (widthPt/heightPt)
  date: { w: 0.16, aspect: null, h: 0.03 }
};
export const FIELD_MIN = { w: 0.04, h: 0.015 };

export const DOCUMENT_REQUEST_STATUS_LABELS: Record<DocumentRequestStatus, string> = {
  draft: 'Not sent',
  sent: 'Sent',
  viewed: 'Viewed',
  signed: 'Signed',
  revoked: 'Revoked'
};
export function getDocumentRequestStatusColor(
  s: DocumentRequestStatus
): 'default' | 'info' | 'warning' | 'success' | 'error' {
  return s === 'sent'
    ? 'info'
    : s === 'viewed'
      ? 'warning'
      : s === 'signed'
        ? 'success'
        : s === 'revoked'
          ? 'error'
          : 'default';
}
